import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, ArrowLeft, Eye, EyeOff, Info, PenLine, RotateCcw, ShieldCheck, X } from 'lucide-react';
import { getThemeColor } from '../../config/appConfig';
import NewtonsCradleLoader from '../../components/common/NewtonsCradleLoader';
import apiService from '../../utils/apiService';
import { useToast } from '../../contexts/ToastContext';
import { AgreementPdfFrame } from './AgreementPdfModal';
import SignaturePad from './SignaturePad';
import { agreementStatusView, agreementTitle, normalizeAgreementDetails } from '../../utils/partnerAgreements';

const THEME = getThemeColor('primary');

const primaryBtn =
  'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed';
const secondaryBtn =
  'inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition disabled:opacity-50';

const ErrorBox = ({ message }) =>
  message ? (
    <div className="flex items-start gap-2 rounded-xl px-4 py-3 text-sm bg-red-50 text-red-700">
      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
      <span>{message}</span>
    </div>
  ) : null;

/**
 * Sign Partner Agreement: draw → review → reset/redraw as often as needed → Confirm & Sign → Confirm Signature.
 * Nothing is submitted while drawing or resetting; only the final drawing is sent, on "Confirm Signature".
 * The API re-validates ownership, agreement state, the drawn ink and the document fingerprint.
 */
const SignAgreementModal = ({ agreement, isOpen, onClose, onSigned }) => {
  const { showSuccess } = useToast();
  const mappingId = agreement?.affiliateAgreementMappingId;
  const padRef = useRef(null);
  const [session, setSession] = useState(null);
  const [starting, setStarting] = useState(true);
  const [startError, setStartError] = useState('');
  const [hasSignature, setHasSignature] = useState(false);
  const [step, setStep] = useState('sign'); // 'sign' | 'confirm'
  const [preview, setPreview] = useState('');
  const [showPdf, setShowPdf] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const loadPdf = useCallback(() => apiService.getAgreementPdf(mappingId, { mode: 'view' }), [mappingId]);

  useEffect(() => {
    if (!isOpen || !mappingId) return undefined;
    let cancelled = false;
    setSession(null);
    setStartError('');
    setSubmitError('');
    setHasSignature(false);
    setStep('sign');
    setPreview('');
    setShowPdf(false);
    setStarting(true);
    apiService.startAgreementSignature(mappingId).then((res) => {
      if (cancelled) return;
      if (res.success && res.data) setSession(res.data);
      else setStartError(res.message || res.error || 'Unable to start the signature');
      setStarting(false);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, mappingId]);

  if (!isOpen) return null;

  const title = agreementTitle(agreement);
  const statusLabel = agreementStatusView(agreement).label;

  const resetSignature = () => {
    padRef.current?.clear();
    setSubmitError('');
  };

  const goToConfirm = () => {
    if (!padRef.current?.hasInk()) {
      setHasSignature(false);
      return;
    }
    setPreview(padRef.current.toDataURL());
    setSubmitError('');
    setStep('confirm');
  };

  const confirmSignature = async () => {
    if (submitting || !padRef.current?.hasInk()) return;
    setSubmitting(true);
    setSubmitError('');
    const res = await apiService.completeAgreementSignature(mappingId, {
      affiliateAgreementSignatureId: session.affiliateAgreementSignatureId,
      signature: padRef.current.getSignature(),
      consent: true,
      documentHash: session.documentHash,
    });
    setSubmitting(false);
    if (res.success) {
      showSuccess('Your Partner Agreement has been successfully signed.', 5000, 'Agreement Signed Successfully');
      onSigned?.(normalizeAgreementDetails(res.data));
      return;
    }
    setSubmitError(res.message || res.error || 'Signature failed. Please try again.');
  };

  // Clicking outside never discards a drawn signature by accident.
  const handleBackdrop = () => {
    if (!submitting && !hasSignature) onClose();
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center sm:p-4">
      <button type="button" className="absolute inset-0 bg-black/50" onClick={handleBackdrop} aria-label="Close" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="sign-agreement-title"
        className="relative w-full sm:max-w-2xl max-h-[100dvh] sm:max-h-[92vh] flex flex-col bg-white shadow-xl border border-gray-100 rounded-t-2xl sm:rounded-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-5 sm:px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-red-50">
              {step === 'confirm' ? (
                <ShieldCheck className="w-5 h-5" style={{ color: THEME }} />
              ) : (
                <PenLine className="w-5 h-5" style={{ color: THEME }} />
              )}
            </div>
            <div className="min-w-0">
              <h2 id="sign-agreement-title" className="text-base sm:text-lg font-semibold text-gray-900">
                {step === 'confirm' ? 'Confirm Your Signature' : 'Sign Partner Agreement'}
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                {step === 'confirm'
                  ? 'Review your signature before it is applied to the Agreement.'
                  : 'Please review the Agreement and provide your signature below.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition disabled:opacity-40"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 space-y-5">
          {starting ? (
            <div className="flex items-center justify-center py-16">
              <NewtonsCradleLoader size="28px" />
            </div>
          ) : startError ? (
            <ErrorBox message={startError} />
          ) : (
            <>
              {/* Agreement information */}
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3.5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-900">{agreement.agreementName}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Version: v{agreement.agreementVersion} <span className="mx-1.5">·</span> Status: {statusLabel}
                    </p>
                  </div>
                  {step === 'sign' ? (
                    <button
                      type="button"
                      onClick={() => setShowPdf((v) => !v)}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
                      style={{ color: THEME }}
                    >
                      {showPdf ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {showPdf ? 'Hide Agreement' : 'View Agreement'}
                    </button>
                  ) : null}
                </div>
                {showPdf && step === 'sign' ? (
                  <div className="mt-3">
                    <AgreementPdfFrame loadPdf={loadPdf} title={title} className="h-[45vh]" />
                  </div>
                ) : null}
              </div>

              {/* Step 1 — draw (kept mounted during confirm so "Go Back & Edit" returns to the same drawing) */}
              <div className={step === 'sign' ? 'space-y-3' : 'hidden'}>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Your Signature</p>
                  <p className="text-xs text-gray-500 mt-0.5">Draw your signature below using your mouse, trackpad or finger.</p>
                </div>
                <SignaturePad ref={padRef} onChange={setHasSignature} />
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs text-gray-500">
                    Signing as <span className="font-semibold text-gray-800">{session.registeredName}</span>
                  </p>
                  <button type="button" onClick={resetSignature} className={secondaryBtn}>
                    <RotateCcw className="w-4 h-4" />
                    Reset Signature
                  </button>
                </div>
                {hasSignature ? (
                  <div className="flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    <Info className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>Please make sure your signature is correct before confirming. Use Reset Signature to draw it again.</span>
                  </div>
                ) : null}
                <p className="text-xs text-gray-500">Your signature will be used to electronically sign this Agreement.</p>
              </div>

              {/* Step 2 — final confirmation */}
              {step === 'confirm' ? (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600">You are about to electronically sign:</p>
                    <p className="text-base font-bold text-gray-900 mt-1">{title}</p>
                  </div>
                  <div className="rounded-xl border-2 border-gray-200 bg-white p-3">
                    <img src={preview} alt="Your signature" className="w-full h-[150px] sm:h-[180px] object-contain" />
                    <div className="border-t border-gray-200 mt-2 pt-2 text-xs text-gray-500">
                      {session.registeredName} — Partner electronic signature
                    </div>
                  </div>
                  <p className="text-sm text-gray-700">
                    Please confirm that the signature shown above is your intended electronic signature.
                  </p>
                  <p className="text-xs leading-relaxed text-gray-500">{session.consentStatement}</p>
                </div>
              ) : null}

              <ErrorBox message={submitError} />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 px-5 sm:px-6 py-4 border-t border-gray-100 bg-white">
          {step === 'sign' || !session ? (
            <>
              <button type="button" onClick={onClose} className={secondaryBtn}>
                Cancel
              </button>
              {session ? (
                <button
                  type="button"
                  onClick={goToConfirm}
                  disabled={!hasSignature}
                  className={primaryBtn}
                  style={{ backgroundColor: THEME }}
                >
                  <PenLine className="w-4 h-4" />
                  Confirm &amp; Sign Agreement
                </button>
              ) : null}
            </>
          ) : (
            <>
              <button type="button" onClick={() => setStep('sign')} disabled={submitting} className={secondaryBtn}>
                <ArrowLeft className="w-4 h-4" />
                Go Back &amp; Edit
              </button>
              <button
                type="button"
                onClick={confirmSignature}
                disabled={submitting}
                className={primaryBtn}
                style={{ backgroundColor: THEME }}
              >
                {submitting ? <NewtonsCradleLoader size="18px" color="#fff" /> : <ShieldCheck className="w-4 h-4" />}
                {submitting ? 'Signing...' : 'Confirm Signature'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignAgreementModal;
